import { ReactNode } from 'react';

import { CardContent } from '#root/components/ui';
import { CanguroGuvery } from '#root/assets';
import styles from './orderLayout.module.scss';

/**
 * Esqueleto común de las tarjetas de Mis Pedidos (regular y ONE): contenido con encabezado y
 * fila de producto. Va dentro de un `Card`; las franjas (`Notice strip`) van después del
 * contenido. En tarjetas angostas (≤520px) el precio y el botón bajan a su propia fila.
 */

/** Contenido de la tarjeta: el layout angosto depende de su ancho, no del de la ventana */
export const OrderContent = ({ children }: { children: ReactNode }) => (
  <CardContent className={styles.content}>{children}</CardContent>
);

interface OrderHeaderProps {
  /** Número público del pedido */
  number: string;
  /** Badges junto al número (ONE, Envío Nacional, estado) */
  badges?: ReactNode;
  /** A la derecha: antigüedad, vencimiento */
  meta?: ReactNode;
  /** Menú de acciones (⋮), al final */
  menu?: ReactNode;
}

export const OrderHeader = ({ number, badges, meta, menu }: OrderHeaderProps) => (
  <div className={styles.header}>
    <span className={styles.number}>Pedido #{number}</span>
    {badges}
    <div className={styles.headerEnd}>
      {meta}
      {menu}
    </div>
  </div>
);

interface OrderProductProps {
  title?: string;
  /** Link del producto: lo abren la foto, el título y el chip del dominio */
  url?: string;
  /** Dominio del link (chip) */
  host?: string;
  image?: string;
  /** Solo se muestra si es mayor que 1 ("×2") */
  quantity?: number | string;
  /** Línea gris bajo el título (observaciones, color) */
  detail?: ReactNode;
  /** Chips después del dominio (condición, empaque) */
  chips?: string[];
  /** Sub-card del precio (PricePair o PriceTotal) */
  price?: ReactNode;
  /** Botón bajo el precio, del mismo ancho */
  action?: ReactNode;
}

export const OrderProduct = (props: OrderProductProps) => {
  const { title, url, host, image, quantity, detail, chips = [], price, action } = props;
  const count = Number(quantity);
  const img = image ? (
    <img className={styles.image} src={image} alt={title || 'Producto'} />
  ) : (
    <img className={`${styles.image} ${styles.holder}`} src={CanguroGuvery} alt="Producto" />
  );
  const extraChips = chips
    .filter(Boolean)
    .map((chip) => chip.charAt(0).toUpperCase() + chip.slice(1));

  return (
    <div className={styles.order}>
      {/* Foto, título y chip abren el link del producto */}
      {url ? (
        <a className={styles.imageLink} href={url} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
      <div className={styles.info}>
        <h3 className={styles.name}>
          {url ? (
            <a className={styles.nameLink} href={url} target="_blank" rel="noopener noreferrer">
              {title}
            </a>
          ) : (
            title
          )}
          {count > 1 && <span className={styles.qty}> ×{count}</span>}
        </h3>
        {detail && <p className={styles.meta}>{detail}</p>}
        {(host || extraChips.length > 0) && (
          <div className={styles.chips}>
            {host && (
              <a
                className={`${styles.chip} ${styles.hostChip}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Link del producto: ${host}`}>
                {host}
              </a>
            )}
            {extraChips.map((chip) => (
              <span key={chip} className={styles.chip}>
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>
      {(price || action) && (
        <div className={styles.side}>
          {price && <div className={styles.price}>{price}</div>}
          {action && <div className={styles.action}>{action}</div>}
        </div>
      )}
    </div>
  );
};
